import { Injectable } from '@angular/core';
import { DataService } from '../data/data.service';
import { HttpService } from '../http/http.service';
import { map, Observable } from 'rxjs';

import {
  IConsentAPIResponse,
  IConsentCreateResponse,
  IConsentErrorresponse,
  IConsentPayload,
  IPreferences,
  IUpdateConsentAPIResponse,
  IUserInformation,
} from '../../interfaces/IPreference';
import { getApiUrlForParams, PREFERENCES_GET_CONSENT_URL, SAVE_CONTACT_INFO_FOR_PROFILE } from '../../utils/api.util';
import { API_CCHS_URL } from '../../components/pages/profile/profile.constant';
import { HttpClient } from '@angular/common/http';
import { ApiContact } from '../../components/pages/profile/pages/edit-contact-information/edit-contact-information.interface';
import { ISendDiscountCode } from '../../interfaces/IBenefits';

@Injectable({
  providedIn: 'root',
})
export class PreferencesService {
  constructor(private http: HttpService, private data: DataService, private httpClient: HttpClient) { }

  getConsent(alternateId: string): Observable<IPreferences | IConsentErrorresponse> {
    return this.http.get<IConsentAPIResponse>(
      `/api/public/user/consent?searchType=myAccountUserId&myAccountUserId=${alternateId}`
    ).pipe(map(v => v.metadata.response));
  }

  getIPAddress(): Observable<IUserInformation> {
    return this.httpClient.get<IUserInformation>(`${API_CCHS_URL}services/utility/whatsmyip`);
  }

  createConsent(payload: IConsentPayload): Observable<IConsentCreateResponse> {
    return this.http.post<IConsentCreateResponse>(PREFERENCES_GET_CONSENT_URL, payload);
  }

  updateConsent(
    profileId: number,
    payload: IConsentPayload
  ): Observable<IUpdateConsentAPIResponse> {
    return this.http.put<IUpdateConsentAPIResponse>(
      `/api/public/user/consent/${profileId}`,
      payload
    );
  }

  sendDiscountCode(emailId: string): Observable<ISendDiscountCode> {
    const url = `/api/${this.data.auth0UserId.getValue()}/contracts/${
      this.data.contractData$.getValue()?.baseContract.contractNumber
    }/appliancebuyline`;
    return this.http.post<ISendDiscountCode>(url, { email: emailId });
  }

  saveContactInfoForProfile(contractNumber: string, payload: ApiContact): Observable<ApiContact> {
    const url = getApiUrlForParams(SAVE_CONTACT_INFO_FOR_PROFILE, { contractNumber });
    return this.http.put<ApiContact>( url , payload);
  }

  getMobileNumberValidity(mobileNumber: string): Observable<{ response: boolean; successFlag: 'Y' | 'N' }> {
    return this.http.get<{ response: boolean; successFlag: 'Y' | 'N' }>(
      `/api/public/user/validateMobileNumber`,
      { params: { phoneNumber: encodeURIComponent(mobileNumber) } }
    );
  }

  /**
   * @description Builds the attributes array for consent payload, including source and optional IP address
   * @param ipAddress
   * @param source
   * @returns
   */
  buildConsentAttributes(ipAddress?: string, source: string = 'MyAccount'): { Key: string, Value: string }[] {
    const attributes = [{ Key: 'source', Value: source }];
    if (ipAddress) {
      attributes.push({ Key: 'User IP Address', Value: ipAddress });
    }
    return attributes;
  }

  /**
   * @description Builds the consent object based on type and status
   * @param consentType
   * @param consentStatus
   * @param attributes
   * @returns
   */
  buildConsent(consentType: string, consentStatus: number, attributes: { Key: string; Value: string }[]): any {
    return { ConsentType: consentType, ConsentStatus: consentStatus, Attributes: attributes };
  }

  /**
   * @description Builds the phone numbers array for consent payload
   * @param phoneNumber
   * @param consentStatus
   * @param ipAddress
   * @returns
   */
  buildPhoneNumbers(phoneNumber: string, consentStatus: number, ipAddress?: string): any[] {
    const attributes = this.buildConsentAttributes(ipAddress);
    return [{
      PhoneType: 'Primary',
      PhoneNumber: phoneNumber,
      Consents: [
        this.buildConsent('Marketing calls', consentStatus, attributes),
        this.buildConsent('Marketing SMS/Text messages', consentStatus, attributes),
      ]
    }];
  }

  /**
   * @description Builds the complete consent payload for creating a new profile
   * @param userContracts
   * @param userEmail
   * @param primaryPhoneNumber
   * @param firstName
   * @param lastName
   * @param consentStatus
   * @param ipAddress
   * @returns
   */
  buildConsentPayload(
    userContracts: string[],
    userEmail: string,
    primaryPhoneNumber: string,
    firstName: string,
    lastName: string,
    consentStatus: number,
    ipAddress?: string
  ): IConsentPayload {
    return {
      CustomFields: [{ Name: 'contract_list', Value: userContracts.join(', ') }],
      PhoneNumbers: this.buildPhoneNumbers(primaryPhoneNumber, consentStatus, ipAddress),
      CustomerType: 'Customer',
      CustomerName: { FirstName: firstName, LastName: lastName },
      AlternateIds: [{ AlternateIdType: 'MyAccount User ID', AlternateId: userEmail }]
    };
  }

  /**
   * @description Builds the consent payload for updating an existing profile
   * @param existingPreferences
   * @param primaryPhoneNumber
   * @param consentStatus
   * @param ipAddress
   * @param userContracts
   * @returns
   */
  buildUpdateConsentPayload(
    existingPreferences: IPreferences,
    primaryPhoneNumber: string,
    consentStatus: number,
    ipAddress?: string,
    userContracts: string[] = []
  ): IConsentPayload {
    const attributes = this.buildConsentAttributes(ipAddress);

    let phoneNumbers = existingPreferences.PhoneNumbers;
    if (phoneNumbers && phoneNumbers.length) {
      phoneNumbers = phoneNumbers.map((v: any) => ({
        PhoneType: v.PhoneType,
        PhoneNumber: primaryPhoneNumber || v.PhoneNumber,
        Consents: v.Consents.map((val: any) => ({
          ConsentType: val.ConsentType,
          ConsentStatus: consentStatus,
          Attributes: attributes
        }))
      }));
    } else {
      phoneNumbers = this.buildPhoneNumbers(primaryPhoneNumber, consentStatus, ipAddress);
    }

    let customFields = existingPreferences.CustomFields ? [...existingPreferences.CustomFields] : [];
    if (userContracts && userContracts.length > 0) {
      const contractListField = customFields.find((f: any) => f.Name === 'contract_list');
      const existingContracts = contractListField?.Value
        ? contractListField.Value.split(',').map((c: string) => c.trim()).filter(Boolean)
        : [];

      const mergedContracts = Array.from(new Set([...existingContracts, ...userContracts]));

      if (contractListField) {
        customFields = customFields.map((f: any) =>
          f.Name === 'contract_list' ? { ...f, Value: mergedContracts.join(', ') } : f
        );
      } else {
        customFields.push({ Name: 'contract_list', Value: mergedContracts.join(', ') });
      }
    }

    return {
      CustomFields: customFields,
      PhoneNumbers: phoneNumbers,
      CustomerType: existingPreferences.CustomerType,
      CustomerName: existingPreferences.CustomerName,
      AlternateIds: existingPreferences.AlternateIds,
    };
  }
}
